import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { setCsrfToken } from '../utils/api';
import { getApiUrl } from '../config/api';
import './Auth.css';

const OAuthSuccess = ({ onAuthSuccess }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Check for errors
        const errorParam = searchParams.get('error');
        if (errorParam) {
          setError(`Authentication failed: ${errorParam}`);
          setTimeout(() => {
            navigate('/auth?error=' + errorParam);
          }, 2000);
          return;
        }

        // Token is now in httpOnly cookie (set by backend)
        // Check if CSRF token was provided in URL (from OAuth callback)
        const csrfParam = searchParams.get('csrf');
        if (csrfParam) {
          setCsrfToken(csrfParam);
        }

        // Fetch user profile to verify authentication
        const response = await fetch(getApiUrl('/api/auth/me'), {
          credentials: 'include', // Send cookie with request
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const data = await response.json();

        // Store CSRF token if provided in response
        if (data.csrfToken) {
          setCsrfToken(data.csrfToken);
        }

        // Store user data in localStorage (non-sensitive, for UI purposes)
        localStorage.setItem('user', JSON.stringify(data.user));

        // Call success callback
        if (onAuthSuccess) {
          onAuthSuccess(data.user);
        }

        // Navigate to dashboard
        setProcessing(false);
        navigate('/dashboard');

      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Authentication failed');
        setTimeout(() => {
          navigate('/auth?error=authentication_failed');
        }, 2000);
      }
    };

    processOAuthCallback();
  }, [searchParams, navigate, onAuthSuccess]);

  return (
    <div className="auth-container">
      <motion.div
        className="auth-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="auth-header">
          <div className="auth-header-logo">
            <div className="jpcc-seal-container">
              <img
                src={`${process.env.PUBLIC_URL}/JPCC_seal_trans_white.png`}
                alt="Jefferson Parish Clerk of Court Seal"
                className="jpcc-seal auth-seal"
              />
            </div>
            <div className="auth-header-text">
              <h1 className="auth-title">Jon A. Gegenheimer</h1>
              <p className="auth-subtitle">Jefferson Parish Clerk of Court</p>
              <p className="auth-subtitle auth-subtitle-secondary">Marriage License Pre-Application</p>
            </div>
          </div>
        </div>

        <div className="oauth-processing">
          {error ? (
            <motion.div
              className="error-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.div>
          ) : (
            <>
              <div className="loader"></div>
              <p className="processing-text">
                {processing ? 'Completing authentication...' : 'Redirecting...'}
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default OAuthSuccess;
